import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect } from 'react';

import { Button, Dropdown, FeatureRadio } from '@/components/chariot-ui';
import { Input, InputEvent } from '@/components/form/Input';
import { useWizard } from '@/components/wizards/hooks/useWizard';
import { useWizardFooterConfig } from '@/components/wizards/hooks/useWizardFooterConfig';
import type { WizardConfig } from '@/components/wizards/types';
import { WizardModalFooter } from '@/components/wizards/WizardModalFooter';
import { WizardProgressBar } from '@/components/wizards/WizardProgressBar';
import { useModifyAccount } from '@/hooks/useAccounts';
import useInitialize, {
  CloudInitializeRequest,
  CloudInitializeResponse,
} from '@/hooks/useInitialize';
import { cn } from '@/utils/classname';
import { triggerConfetti } from '@/utils/confetti.util';
import { downloadIACTemplate } from '@/utils/download.util';
import { useStorage } from '@/utils/storage/useStorage.util';

const DEV_TYPE_OPTIONS = [
  { label: 'Cloudformation (Recommended)', value: 'cloudformation' },
  { label: 'Terraform', value: 'terraform' },
  { label: 'Manual', value: 'manual' },
];

const CALL_AS_OPTIONS = [
  { label: 'Management Account', value: 'SELF' },
  { label: 'Delegated Admin Account', value: 'DELEGATED_ADMIN' },
];

const TARGET_PATTERN = /^(ou-[a-z0-9]{4,32}-[a-z0-9]{8,32}|r-[a-z0-9]{4,32})$/;

function validateTargets(targets: string): string | null {
  if (!targets) return null; // empty is valid (single account deploy)
  const items = targets.split(',');
  for (const item of items) {
    if (!TARGET_PATTERN.test(item)) {
      return `Invalid target: "${item}". Must be a comma-separated list of items that each match r-xxxx or ou-xxxx-xxxxxxxx`;
    }
  }
  return null;
}

type SelectionType = 'organization' | 'individual';

type FormState = {
  organization: {
    org_account_id: string;
    deployment_type: string;
    call_as: string;
    targets: string;
  };
  individual: { account_id: string; deployment_type: string };
};

type AWSWizardData = {
  selection: SelectionType | undefined;
  form: FormState;
  hasDownloaded: boolean;
  savedState?: {
    request: CloudInitializeRequest;
    response: CloudInitializeResponse;
  };
  isComplete: boolean;
};

export function AWS({ onClose }: { onClose: () => void }) {
  const [savedState, setSavedState] = useStorage<
    | {
        request: CloudInitializeRequest;
        response: CloudInitializeResponse;
      }
    | undefined
  >({ key: 'aws-integration-state' });

  const { mutateAsync: initializeCloud, status: initializeCloudStatus } =
    useInitialize();
  const { mutateAsync: link, status: linkStatus } = useModifyAccount('link');

  // Wizard configuration with 2 steps
  // Note: React Compiler handles optimization - manual useMemo not needed
  const wizardConfig: WizardConfig<AWSWizardData> = {
    steps: [
      {
        id: 'scope-config',
        order: 1,
        title: 'Scope & Configuration',
        validate: data => {
          if (!data.selection) return false;
          const accountId =
            data.selection === 'organization'
              ? data.form.organization.org_account_id
              : data.form.individual.account_id;
          if (!accountId || !data.form[data.selection].deployment_type)
            return false;
          if (data.selection === 'organization') {
            const targetsError = validateTargets(
              data.form.organization.targets
            );
            if (targetsError) return false;
          }
          return true;
        },
      },
      {
        id: 'download-deploy',
        order: 2,
        title: 'Download/Deploy',
        isFinal: true,
        validate: data => {
          if (!data.selection) return false;
          const deploymentType = data.form[data.selection].deployment_type;
          return deploymentType === 'manual' || data.hasDownloaded;
        },
      },
    ],
    initialData: {
      selection: savedState?.request.org_account_id
        ? 'organization'
        : savedState?.request.account_id
          ? 'individual'
          : 'organization',
      form: {
        organization: {
          org_account_id: savedState?.request.org_account_id || '',
          deployment_type: savedState?.request.deployment_type || '',
          call_as: savedState?.request.call_as || 'SELF',
          targets: savedState?.request.targets || '',
        },
        individual: {
          account_id: savedState?.request.account_id || '',
          deployment_type: savedState?.request.deployment_type || '',
        },
      },
      hasDownloaded: false,
      savedState,
      isComplete: false,
    },
    onSubmit: async data => {
      await handleFinish(data);
    },
  };

  const wizard = useWizard(wizardConfig);

  const {
    formData,
    setFormData,
    navigation,
    progress,
    isSubmitting,
    currentStepId,
  } = wizard;

  // Handle input changes for accountId
  const handleInputChange = useCallback(
    (type: SelectionType, field: string) => (e: InputEvent) => {
      setFormData(prev => ({
        ...prev,
        form: {
          ...prev.form,
          [type]: {
            ...prev.form[type],
            [field]: e.target.value,
          },
        },
      }));
    },
    [setFormData]
  );

  // Handle Dropdown selection for deployment_type
  const handleDropdownSelect = useCallback(
    (type: SelectionType) => (option: { value?: string | number }) => {
      if (typeof option.value === 'string') {
        setFormData(prev => ({
          ...prev,
          form: {
            ...prev.form,
            [type]: {
              ...prev.form[type],
              deployment_type: option.value as string,
            },
          },
        }));
      }
    },
    [setFormData]
  );

  const handleCallAsSelect = useCallback(
    (option: { value?: string | number }) => {
      if (typeof option.value === 'string') {
        setFormData(prev => ({
          ...prev,
          form: {
            ...prev.form,
            organization: {
              ...prev.form.organization,
              call_as: option.value as string,
            },
          },
        }));
      }
    },
    [setFormData]
  );

  // Find the selected deployment_type option for Dropdown
  const getSelectedDropdownOption = useCallback(
    (type: SelectionType) => {
      const val = formData.form[type].deployment_type;
      return val ? [DEV_TYPE_OPTIONS.find(opt => opt.value === val)!] : [];
    },
    [formData.form]
  );

  const getSelectedCallAsOption = useCallback(() => {
    const val = formData.form.organization.call_as;
    return val ? [CALL_AS_OPTIONS.find(opt => opt.value === val)!] : [];
  }, [formData.form.organization.call_as]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!formData.selection) return;

      const request = {
        provider: 'aws',
        ...formData.form[formData.selection],
      };

      const response = await initializeCloud(request);
      const newSavedState = {
        request,
        response,
      };
      setSavedState(newSavedState);
      setFormData(prev => ({
        ...prev,
        savedState: newSavedState,
      }));
      navigation.handleNext();
    },
    [
      formData.selection,
      formData.form,
      initializeCloud,
      setSavedState,
      setFormData,
      navigation,
    ]
  );

  // Handle Next button for Step 1 - calls API before advancing
  const handleNextStep1 = useCallback(async () => {
    if (!formData.selection) return;

    const request = {
      provider: 'aws',
      ...formData.form[formData.selection],
    };

    const response = await initializeCloud(request);
    const newSavedState = {
      request,
      response,
    };
    setSavedState(newSavedState);
    setFormData(prev => ({
      ...prev,
      savedState: newSavedState,
    }));
    navigation.handleNext();
  }, [
    formData.selection,
    formData.form,
    initializeCloud,
    setSavedState,
    setFormData,
    navigation,
  ]);

  const handleCancel = useCallback(() => {
    setSavedState(undefined);
    wizard.reset();
    onClose();
  }, [setSavedState, wizard, onClose]);

  const handleFinish = useCallback(
    async (data: AWSWizardData) => {
      if (!data.selection) return;

      await link({
        username: 'amazon',
        config:
          data.selection === 'organization'
            ? {
                org_account_id: data.form.organization.org_account_id,
                external_id: data.savedState?.response?.username,
              }
            : {
                account_id: data.form.individual.account_id,
                external_id: data.savedState?.response?.username,
              },
        value:
          data.selection === 'organization'
            ? data.form.organization.org_account_id
            : data.form.individual.account_id,
      });

      setSavedState(undefined);
      setFormData(prev => ({ ...prev, isComplete: true }));
    },
    [link, setSavedState, setFormData]
  );

  // Trigger confetti effect when setup is complete
  useEffect(() => {
    if (formData.isComplete) {
      triggerConfetti({ delay: 300 });
    }
  }, [formData.isComplete]);

  // Wizard footer configuration
  const footerConfig = useWizardFooterConfig({
    canGoNext: navigation.canGoNext,
    canGoBack: navigation.canGoBack,
    isPending:
      isSubmitting ||
      initializeCloudStatus === 'pending' ||
      linkStatus === 'pending',
    isLastStep: navigation.isLastStep,
    onNext:
      currentStepId === 'scope-config'
        ? handleNextStep1
        : navigation.handleNext,
    onBack: () => {
      setFormData(prev => ({
        ...prev,
        hasDownloaded: false,
        savedState: undefined,
      }));
      setSavedState(undefined);
      navigation.handleBack();
    },
    onCancel: handleCancel,
    onSubmit: wizard.submit,
    labels: {
      next: 'Next',
      submit: 'Complete Setup',
      submitting: 'Completing...',
      cancel: 'Cancel',
      back: 'Back',
    },
  });

  return (
    <div className="">
      <div className="bg-chariot-stroke mb-6 h-[2px] w-full shrink-0 rounded-lg" />

      {/* Progress indicator */}
      {!formData.isComplete && (
        <WizardProgressBar
          currentStep={progress.currentDisplayStep}
          totalSteps={progress.totalDisplaySteps}
          percentage={progress.percentage}
          stepTitle={progress.currentStepTitle}
        />
      )}

      {/* Step 1: Scope & Configuration */}
      {currentStepId === 'scope-config' && (
        <>
          <div className="mb-4 font-bold">Scope of Integration</div>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 [&>button]:pr-4 [&>button>.text-left]:w-full"
          >
            <FeatureRadio
              id="aws-radio-organization"
              title="Organization"
              description="Integrates all accounts within the organization"
              checked={formData.selection === 'organization'}
              onChange={() =>
                setFormData(prev => ({ ...prev, selection: 'organization' }))
              }
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-2">
                  <label className="text-sm font-bold">Account ID</label>
                  <Input
                    className="bg-transparent"
                    name="accountId"
                    value={formData.form.organization.org_account_id}
                    onChange={handleInputChange(
                      'organization',
                      'org_account_id'
                    )}
                    placeholder="000000000000"
                    required
                  />
                  <label className="text-chariot-text-primary mb-1 text-sm font-semibold">
                    Deployment Authority
                  </label>
                  <Dropdown
                    className="[&>button]:w-full [&>button]:justify-between"
                    options={CALL_AS_OPTIONS}
                    selected={getSelectedCallAsOption()}
                    onSelect={handleCallAsSelect}
                    placeholder="Select deployment authority"
                  />
                  <label className="text-chariot-text-primary mb-1 text-sm font-semibold">
                    Deployment Type
                  </label>
                  <Dropdown
                    className="[&>button]:w-full [&>button]:justify-between"
                    options={DEV_TYPE_OPTIONS}
                    selected={getSelectedDropdownOption('organization')}
                    onSelect={handleDropdownSelect('organization')}
                    placeholder="Select deployment type"
                  />
                  <label className="text-chariot-text-primary mb-1 text-sm font-semibold">
                    Target Root/OUs
                  </label>
                  <Input
                    className="bg-transparent"
                    name="targets"
                    value={formData.form.organization.targets}
                    onChange={handleInputChange('organization', 'targets')}
                    placeholder="r-xxxx or ou-xxxx-xxxxxxxx"
                  />
                  {formData.form.organization.targets &&
                  validateTargets(formData.form.organization.targets) ? (
                    <span className="text-xs text-red-500">
                      {validateTargets(formData.form.organization.targets)}
                    </span>
                  ) : (
                    <span className="text-chariot-text-secondary text-xs">
                      Comma-separated list of root or OU IDs to deploy across
                    </span>
                  )}
                </div>
              }
            />
            <FeatureRadio
              id="aws-radio-individual"
              title="Individual Account"
              description="Integrates a single account"
              checked={formData.selection === 'individual'}
              onChange={() =>
                setFormData(prev => ({ ...prev, selection: 'individual' }))
              }
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-2">
                  <label className="text-sm font-bold">Account ID</label>
                  <Input
                    className="bg-transparent"
                    name="accountId"
                    value={formData.form.individual.account_id}
                    onChange={handleInputChange('individual', 'account_id')}
                    placeholder="000000000000"
                    required
                  />
                  <label className="text-chariot-text-primary mb-1 text-sm font-semibold">
                    Deployment Type
                  </label>
                  <Dropdown
                    className="[&>button]:w-full [&>button]:justify-between"
                    options={DEV_TYPE_OPTIONS}
                    selected={getSelectedDropdownOption('individual')}
                    onSelect={handleDropdownSelect('individual')}
                    placeholder="Select deployment type"
                  />
                </div>
              }
            />
          </form>
        </>
      )}

      {/* Step 2: Download/Deploy */}
      {currentStepId === 'download-deploy' && !formData.isComplete && (
        <div>
          {formData.selection &&
          formData.form[formData.selection].deployment_type ===
            'cloudformation' ? (
            <>
              <div className="mb-4 font-bold">Deploy to AWS</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                Click the button below to deploy the CloudFormation template
                directly to your AWS account. For more information, please
                reference our{' '}
                <a
                  href="https://docs.praetorian.com/articles/37942735313691-amazon-web-services-iac-deployment-recommended"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  AWS documentation
                </a>
                .
              </div>

              {formData.savedState?.response?.quick_create_url && (
                <>
                  <div className="text-chariot-text-secondary mb-2 text-sm font-semibold">
                    Option 1: Deploy in AWS Console
                  </div>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-chariot-text-secondary flex items-center gap-2 text-sm">
                      <InformationCircleIcon className="text-chariot-stroke size-4" />
                      <span>This link expires in 7 days</span>
                    </div>
                  </div>
                  <Button
                    size="xs"
                    styleType="rounded"
                    label="Deploy to AWS Console"
                    endIcon={
                      formData.hasDownloaded ? (
                        <CheckCircleIcon className="text-chariot-stroke-info size-4" />
                      ) : (
                        <ChevronRightIcon className="size-4 stroke-[3px]" />
                      )
                    }
                    onClick={() => {
                      if (formData.savedState?.response?.quick_create_url) {
                        window.open(
                          formData.savedState.response.quick_create_url,
                          '_blank',
                          'noopener,noreferrer'
                        );
                        setFormData(prev => ({ ...prev, hasDownloaded: true }));
                      }
                    }}
                    className={cn(
                      'mb-8',
                      formData.hasDownloaded &&
                        'border-chariot-stroke-info text-chariot-stroke-info hover:bg-chariot-status-info/20 hover:text-chariot-stroke-info border-dashed'
                    )}
                  />
                </>
              )}

              <div className="text-chariot-text-secondary mb-2 text-sm font-semibold">
                {formData.savedState?.response?.quick_create_url
                  ? 'Option 2: Download Template'
                  : 'Download Template'}
              </div>
              <div className="mb-4 flex items-center gap-2">
                <div className="text-chariot-text-secondary flex items-center gap-2 text-sm">
                  <InformationCircleIcon className="text-chariot-stroke size-4" />
                  <span>
                    Download the CloudFormation template to deploy manually
                  </span>
                </div>
              </div>
              <Button
                size="xs"
                styleType="rounded"
                label="Download CloudFormation Template"
                endIcon={
                  formData.hasDownloaded ? (
                    <CheckCircleIcon className="text-chariot-stroke-info size-4" />
                  ) : (
                    <ChevronRightIcon className="size-4 stroke-[3px]" />
                  )
                }
                onClick={() => {
                  if (
                    formData.savedState?.response?.template &&
                    formData.selection
                  ) {
                    const point =
                      formData.selection === 'organization'
                        ? formData.form.organization.org_account_id
                        : formData.form.individual.account_id;

                    downloadIACTemplate({
                      template: formData.savedState.response.template,
                      filename: `guard-aws-${point}`,
                      deploymentType: 'cloudformation',
                    });
                    setFormData(prev => ({ ...prev, hasDownloaded: true }));
                  }
                }}
                className={cn(
                  'mb-8',
                  formData.hasDownloaded &&
                    'border-chariot-stroke-info text-chariot-stroke-info hover:bg-chariot-status-info/20 hover:text-chariot-stroke-info border-dashed'
                )}
              />
            </>
          ) : formData.selection &&
            formData.form[formData.selection].deployment_type ===
              'terraform' ? (
            <>
              <div className="mb-4 font-bold">Download Terraform Template</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                To finish AWS Terraform setup, please download the Terraform
                template, then click finish. For more information, please
                reference our{' '}
                <a
                  href="https://docs.praetorian.com/articles/37942735313691-amazon-web-services-iac-deployment-recommended"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  AWS documentation
                </a>
                .
              </div>

              <div className="mb-8 flex items-center gap-2">
                <div className="text-chariot-text-secondary flex items-center gap-2 text-sm">
                  <InformationCircleIcon className="text-chariot-stroke size-4" />
                  <span>Must download template before finishing</span>
                </div>
              </div>
              <Button
                size="xs"
                styleType="rounded"
                label="Download Terraform Template"
                endIcon={
                  formData.hasDownloaded ? (
                    <CheckCircleIcon className="text-chariot-stroke-info size-4" />
                  ) : (
                    <ChevronRightIcon className="size-4 stroke-[3px]" />
                  )
                }
                onClick={() => {
                  if (
                    formData.savedState?.response?.template &&
                    formData.selection
                  ) {
                    const point =
                      formData.selection === 'organization'
                        ? formData.form.organization.org_account_id
                        : formData.form.individual.account_id;

                    downloadIACTemplate({
                      template: formData.savedState.response.template,
                      filename: `guard-aws-${point}`,
                      deploymentType: 'terraform',
                    });
                    setFormData(prev => ({ ...prev, hasDownloaded: true }));
                  }
                }}
                className={cn(
                  'mb-8',
                  formData.hasDownloaded &&
                    'border-chariot-stroke-info text-chariot-stroke-info hover:bg-chariot-status-info/20 hover:text-chariot-stroke-info border-dashed'
                )}
              />
            </>
          ) : (
            <div>
              <div className="mb-4 font-bold">Manual Setup</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                For manual setup, please follow our{' '}
                <a
                  href="https://docs.praetorian.com/articles/37942735313691-amazon-web-services-iac-deployment-recommended"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  AWS documentation
                </a>{' '}
                to configure your AWS environment.
              </div>
              {formData.savedState?.response?.username && (
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-bold">
                    External ID
                  </label>
                  <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-3">
                    <code className="text-chariot-text-primary text-sm">
                      {formData.savedState.response.username}
                    </code>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Success screen */}
      {formData.isComplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center py-12 text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-chariot-text-primary mb-2 text-2xl font-bold"
          >
            Hooray! You&apos;re All Set!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-chariot-text-secondary mb-8 text-sm"
          >
            Your AWS setup is complete. Chariot will now scan and report on this
            environment
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Button
              styleType="primary"
              label="Done"
              onClick={handleCancel}
              size="sm"
              className="px-8"
            />
          </motion.div>
        </motion.div>
      )}

      {/* Wizard footer with navigation */}
      {!formData.isComplete && (
        <WizardModalFooter config={footerConfig} className="mt-6" />
      )}
    </div>
  );
}
